// src/components/GoogleAnalytics.js
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { safeLog } from '../utils/hydrationSafe';
import { trackPageView, initializeAnalytics, isAnalyticsEnabled } from '../utils/analytics';
import { getCookiePreferences } from '../utils/cookieManager';

/**
 * GDPR-compliant Google Analytics component
 * Only tracks pageviews when user has consented to analytics cookies
 * This should be included once in your app (typically in App.js)
 */
const GoogleAnalytics = () => {
  const location = useLocation();
  const [analyticsInitialized, setAnalyticsInitialized] = useState(false);
  
  // Initialize analytics when component mounts and check for consent changes
  useEffect(() => {
    const initAnalytics = async () => {
      const preferences = getCookiePreferences();
      if (preferences.analytics && !analyticsInitialized) {
        try {
          await initializeAnalytics();
          setAnalyticsInitialized(true);
          safeLog('Google Analytics initialized with user consent');
        } catch (error) {
          console.error('Failed to initialize analytics:', error);
        }
      }
    };

    initAnalytics();

    // Listen for changes to cookie preferences
    const handleStorageChange = () => {
      const preferences = getCookiePreferences();
      if (!preferences.analytics && analyticsInitialized) {
        setAnalyticsInitialized(false);
        safeLog('Google Analytics disabled - user withdrew consent');
      } else if (preferences.analytics && !analyticsInitialized) {
        initAnalytics();
      }
    };

    // Listen for custom cookie preference events (same tab)
    const handleCookiePreferenceChange = (event) => {
      const preferences = event.detail;
      if (!preferences.analytics && analyticsInitialized) {
        setAnalyticsInitialized(false);
        safeLog('Google Analytics disabled - user withdrew consent');
      } else if (preferences.analytics && !analyticsInitialized) {
        initAnalytics();
      }
    };

    // Listen to both storage events (other tabs) and custom events (same tab)
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('cookiePreferencesChanged', handleCookiePreferenceChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('cookiePreferencesChanged', handleCookiePreferenceChange);
    };
  }, [analyticsInitialized]);
  
  // Track pageviews only when analytics is enabled and initialized
  useEffect(() => {
    // Only run in browser environment
    if (typeof window === 'undefined') return;
    
    // Only track if user has consented to analytics
    if (!isAnalyticsEnabled() || !analyticsInitialized) {
      safeLog('Pageview not tracked - analytics not enabled or not consented');
      return;
    }
    
    // Format the path to ensure consistency (e.g., remove trailing slash)
    const path = location.pathname.replace(/\/$/, '') + location.search;
    
    // Get more detailed page information (safely access browser APIs)
    const pageTitle = (typeof document !== 'undefined' && document.title) || 'OriginSelect';
    const countryCode = location.pathname.includes('/country/') ? 
      location.pathname.split('/country/')[1].split('/')[0] : 
      'canada'; // Default to canada if not specified
      
    // Use our consent-aware tracking function
    trackPageView(pageTitle, window.location?.href || '');
    
    // For debug purposes - only log in development and not during prerendering
    safeLog('GA pageview tracked (with consent):', path, {
      country: countryCode, 
      page_type: getPageType(location.pathname)
    });
  }, [location.pathname, location.search, analyticsInitialized]);

  // Helper function to determine page type for better analytics categorization
  const getPageType = (pathname) => {
    if (pathname.includes('/home')) return 'home';
    if (pathname.includes('/privacy')) return 'legal';
    if (pathname.includes('/disclaimer')) return 'legal';
    if (pathname.includes('/accessibility')) return 'legal';
    if (pathname.includes('/products/')) return 'product_detail';
    if (pathname.includes('/products')) return 'product_list';
    if (pathname.includes('/search')) return 'search';
    if (pathname.includes('/about')) return 'about';
    if (pathname.includes('/contact')) return 'contact';
    if (pathname.includes('/login')) return 'account';
    if (pathname.includes('/chat')) return 'engagement';
    if (pathname.includes('/favorites')) return 'engagement';
    return 'other';
  };

  // This component doesn't render anything
  return null;
};

export default GoogleAnalytics;
